import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { CreditCard, ArrowUpRight, ShieldCheck, Receipt } from "lucide-react";

const PLAN_NAMES = { team: "Team", growth: "Growth", command: "Command", enterprise: "Enterprise" };

const fmtMoney = (cents, cur) => `${(cents / 100).toLocaleString(undefined, { style: "currency", currency: (cur || "usd").toUpperCase() })}`;
const fmtDate = (iso) => { const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); };

export default function Billing() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/billing/summary").then((r) => setData(r.data)).catch(() => setData({}));
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/cancel");
      if (r.data?.stripe_cancelled === false) toast.warning("Plan removed here, but the Stripe subscription needs attention — contact support if you're billed again");
      else toast.success("Plan cancelled — you're back on the Starter trial terms");
      setConfirming(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel — please try again");
    } finally { setBusy(false); }
  };

  if (!data) return <Layout><p className="text-muted-foreground" data-testid="billing-loading">Loading…</p></Layout>;

  const isStaff = ["admin", "supervisor"].includes(data.role);
  const planName = data.plan ? PLAN_NAMES[data.plan] || data.plan : null;
  const paid = (data.payments || []).filter((p) => p.payment_status === "paid" || p.payment_status === "refunded");

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="billing-page">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-3 py-1">
          <CreditCard className="h-3.5 w-3.5" /> Billing
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">Your plan &amp; invoices.</h1>

        <div className="mt-8 rounded-2xl border border-border p-6" data-testid="billing-plan-card">
          {isStaff ? (
            <p className="text-muted-foreground flex items-center gap-2" data-testid="billing-staff-note"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Admins and supervisors have full access — no plan needed.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Current plan</p>
                  <p className="font-display text-2xl font-extrabold mt-1" data-testid="billing-plan-name">
                    {planName || (data.trial_days_left > 0 ? `Starter trial · ${data.trial_days_left} day${data.trial_days_left === 1 ? "" : "s"} left` : "Starter trial · ended")}
                  </p>
                  {data.monthly_price && <p className="text-sm text-muted-foreground mt-0.5" data-testid="billing-plan-price">${data.monthly_price}/month</p>}
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Portfolios</p>
                  <p className="font-display text-2xl font-extrabold mt-1 tabular-nums" data-testid="billing-usage">{data.portfolios}<span className="text-muted-foreground text-base font-semibold"> / {data.cap ?? "∞"}</span></p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-6">
                <Button className="gap-1.5" onClick={() => nav("/pricing")} data-testid="billing-change-plan">
                  {data.plan ? "Change plan" : "Choose a plan"} <ArrowUpRight className="h-4 w-4" />
                </Button>
                {data.plan && !confirming && (
                  <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => setConfirming(true)} data-testid="billing-cancel">Cancel plan</Button>
                )}
                {data.plan && confirming && (
                  <span className="inline-flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-1.5 text-sm" data-testid="billing-cancel-confirm">
                    Cancel {planName}? Your work stays safe, but new projects will need a plan again.
                    <Button size="sm" variant="destructive" disabled={busy} onClick={cancel} data-testid="billing-cancel-yes">{busy ? "Cancelling…" : "Yes, cancel"}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} data-testid="billing-cancel-no">Keep it</Button>
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        <div className="mt-8" data-testid="billing-invoices">
          <h2 className="font-display text-lg font-extrabold tracking-tight flex items-center gap-2"><Receipt className="h-4 w-4 text-primary" /> Payments</h2>
          {paid.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-3" data-testid="billing-no-invoices">No payments yet — anything you pay for will show up here.</p>
          ) : (
            <div className="mt-3 border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Plan</th><th className="px-4 py-2.5">Amount</th><th className="px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paid.map((p, i) => (
                    <tr key={i} className="border-t border-border/60" data-testid={`billing-invoice-${i}`}>
                      <td className="px-4 py-2.5">{fmtDate(p.created_at)}</td>
                      <td className="px-4 py-2.5">{PLAN_NAMES[p.plan] || p.plan}</td>
                      <td className="px-4 py-2.5 tabular-nums">{fmtMoney(p.amount, p.currency)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${p.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.payment_status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
